import * as React from 'react';

import SelectedPersonRow from '@/components/organism/CreateSix/shared/SelectedPersonRow';
import ExternalInvitationTabs from '@/components/organism/CreateSix/steps/StepExternal/ExternalInvitationTabs';
import ExternalInvitationContactsPanel from '@/components/organism/CreateSix/steps/StepExternal/ExternalInvitationContactsPanel';
import ExternalInvitationManualPanel from '@/components/organism/CreateSix/steps/StepExternal/ExternalInvitationManualPanel';
import type { ExternalInvitationStepProps } from '@/types/ExternalInvitationStep.types';
import { classNames } from '@/utils/classNames.utils';

export function ExternalInvitationStep(props: ExternalInvitationStepProps): React.JSX.Element {
  const {
    title = 'External invitations',
    description = (
      <span>
        Add people outside your organization to participate in this SIX. If the desired contact does not appear in the list below,
         invite them by entering their information in the “New Invitation” tab. New external users will receive a Welcome email once the SIX is created.
      </span>
    ),

    activeTab,
    onTabChange,

    contactsSearchValue,
    onContactsSearchValueChange,
    contactRows,
    onAddContact,
    selectedParticipants,
    onRemoveSelectedParticipant,

    manualRows,
    onManualRowValuesChange,
    onAddManualRow,
    onRemoveManualRow,
    onAddRowToSix,

    className,
  } = props;

  const baseIdentifier = React.useId();

  const contactsTabIdValue = `${baseIdentifier}-contacts-tab`;
  const manualTabIdValue = `${baseIdentifier}-manual-tab`;
  const contactsPanelIdValue = `${baseIdentifier}-contacts-panel`;
  const manualPanelIdValue = `${baseIdentifier}-manual-panel`;

  const contactsTabIsActive = activeTab === 'contacts';
  const manualTabIsActive = activeTab === 'manual';

  return (
    <div className={classNames('space-y-6 overflow-x-hidden', className)}>
      <div>
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">
            Selected External Participants ({selectedParticipants.length})
          </h4>
        </div>

        <div className="space-y-2">
          {selectedParticipants.map((participant) => (
            <SelectedPersonRow
              key={participant.id}
              name={participant.name}
              email={participant.email}
              onRemove={() => {
                onRemoveSelectedParticipant(participant.id);
              }}
            />
          ))}
        </div>
      </div>

      <div className="w-full">
        <ExternalInvitationTabs
          activeTab={activeTab}
          onTabChange={onTabChange}
          contactsTabId={contactsTabIdValue}
          manualTabId={manualTabIdValue}
          contactsPanelId={contactsPanelIdValue}
          manualPanelId={manualPanelIdValue}
        />

        <ExternalInvitationContactsPanel
          isActive={contactsTabIsActive}
          tabId={contactsTabIdValue}
          panelId={contactsPanelIdValue}
          contactsSearchValue={contactsSearchValue}
          onContactsSearchValueChange={onContactsSearchValueChange}
          contactRows={contactRows}
          onAddContact={onAddContact}
        />

        <ExternalInvitationManualPanel
          isActive={manualTabIsActive}
          tabId={manualTabIdValue}
          panelId={manualPanelIdValue}
          manualRows={manualRows}
          onManualRowValuesChange={onManualRowValuesChange}
          onAddManualRow={onAddManualRow}
          onRemoveManualRow={onRemoveManualRow}
          onAddRowToSix={onAddRowToSix}
        />
      </div>
    </div>
  );
}

ExternalInvitationStep.displayName = 'ExternalInvitationStep';

export default ExternalInvitationStep;
